import { useEffect, useState, type FormEvent } from 'react'
import type { ShiftDashboardView } from '@/api/shiftDashboardApi'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { MAX_DASHBOARD_NAME_LENGTH } from '@/lib/dashboardLayouts'
import { validateViewName } from '@/lib/shiftDashboard'

/**
 * Names a shared shift view: a new one (saving the filters and tiles on
 * screen) or an existing one being renamed. The name is checked here for the
 * quick answer and again by the server, which owns uniqueness.
 */
export function ShiftViewNameDialog({
  open,
  onOpenChange,
  views,
  editing,
  saving,
  error,
  onSubmit,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  views: ShiftDashboardView[]
  /** The view being renamed; null for a new one. */
  editing: ShiftDashboardView | null
  saving: boolean
  /** The server's refusal, when there is one. */
  error: string | null
  onSubmit: (name: string) => void
}) {
  const [name, setName] = useState('')
  const [touched, setTouched] = useState(false)

  // Keyed on the id, not the object: a background refetch of the views must
  // not wipe what is being typed. The name is read only when the dialog opens.
  const editingId = editing?.id ?? null
  const editingName = editing?.name ?? ''
  useEffect(() => {
    if (!open) return
    setName(editingName)
    setTouched(false)
  }, [open, editingId])

  const problem = validateViewName(name, views, editing?.id ?? null)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)
    if (!problem) onSubmit(name.trim())
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <form onSubmit={submit} className='space-y-4'>
          <DialogHeader>
            <DialogTitle>{editing ? 'Rename shift view' : 'New shift view'}</DialogTitle>
            <DialogDescription>
              {editing
                ? 'Everyone who opens the Shift Dashboard sees the new name.'
                : 'Saves the filters and tiles on screen as a view everyone in this tenant can open.'}
            </DialogDescription>
          </DialogHeader>
          <div className='grid gap-1.5'>
            <Label htmlFor='shift-view-name'>Name</Label>
            <Input
              id='shift-view-name'
              value={name}
              maxLength={MAX_DASHBOARD_NAME_LENGTH + 10}
              placeholder='e.g. Day shift — Micro'
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setTouched(true)}
            />
            {touched && problem && <p className='text-xs text-destructive'>{problem}</p>}
            {error && <p className='text-xs text-destructive'>{error}</p>}
          </div>
          <DialogFooter>
            <Button type='button' variant='ghost' onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type='submit' disabled={saving || (touched && !!problem)}>
              {editing ? 'Rename' : 'Create view'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
